import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Check } from "lucide-react";
import { useLang, UI } from "../i18n";
import { OFFRE, THEMES, IMG } from "../content";
import { useLenis, Reveal } from "../components/motion";
import { PageHero } from "../components/PageHero";
import { MiniSite, CourtLines, MonoTag } from "../components/motifs";
import { ContactSection } from "../components/Layout";

// L'OFFRE — 3-tier service skeleton + conversion funnel
export default function Offre() {
  useLenis();
  const { t } = useLang();
  return (
    <MiniSite theme={THEMES.base}>
      <PageHero
        code="AST · OFFRE"
        overline={t(UI.nav.offre)}
        title={t(OFFRE.title)}
        subtitle={t(OFFRE.intro)}
        image={IMG.coach}
      />

      {/* FUNNEL */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 pb-16">
        <Reveal>
          <MonoTag style={{ color: "#F6B02C" }}>{t(OFFRE.funnelTitle)}</MonoTag>
          <div data-testid="offer-funnel" className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-3">
            {OFFRE.funnel.map((s, i) => (
              <div key={i} className="relative flex items-center gap-3 rounded-md border border-white/10 px-5 py-5"
                style={{ backgroundColor: "var(--ms-surface)" }}>
                <span className="font-display text-3xl text-flame">{i + 1}</span>
                <span className="font-heading text-sm md:text-base font-semibold uppercase">{t(s)}</span>
                {i < 3 && <ArrowRight size={16} className="hidden lg:block absolute -right-3 top-1/2 -translate-y-1/2 text-white/40 z-10" />}
              </div>
            ))}
          </div>
        </Reveal>
      </section>

      {/* TIERS */}
      <section className="relative overflow-hidden pb-24 md:pb-32">
        <CourtLines className="-right-56 bottom-0 w-[640px] h-[640px]" opacity={0.05} />
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {OFFRE.tiers.map((tier, i) => (
            <Reveal key={tier.n} delay={i * 0.1}>
              <div data-testid={`tier-${i + 1}`} className="h-full flex flex-col rounded-md border border-white/10 p-8"
                style={{ backgroundColor: "var(--ms-surface)", borderTop: `4px solid ${tier.color}` }}>
                <div className="flex items-baseline gap-3">
                  <span className="font-display text-5xl" style={{ color: tier.color }}>{tier.n}</span>
                  <MonoTag className="text-white/40">TIER {i + 1}</MonoTag>
                </div>
                <h2 className="font-display text-2xl md:text-3xl uppercase leading-tight mt-4">{t(tier.title)}</h2>
                <p className="font-body text-white/65 mt-4">{t(tier.desc)}</p>
                <ul className="mt-6 space-y-3 flex-1">
                  {tier.items.map((it, k) => (
                    <li key={k} className="flex gap-3 items-start">
                      <span className="mt-1 rounded-full p-0.5" style={{ backgroundColor: tier.color }}>
                        <Check size={12} className="text-black" />
                      </span>
                      <span className="font-body text-sm text-white/85">{t(it)}</span>
                    </li>
                  ))}
                </ul>
                <Link to={tier.to} data-testid={`tier-cta-${i + 1}`}
                  className="inline-flex items-center justify-center gap-2 mt-8 rounded-full px-6 py-3 font-heading font-semibold uppercase text-xs tracking-wide text-black hover:opacity-85 transition-opacity duration-300"
                  style={{ backgroundColor: tier.color }}>
                  {t(UI.cta.discover)} <ArrowUpRight size={16} />
                </Link>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <ContactSection defaultProgram={t({ fr: "L'Offre Airsports Travel", en: "The Airsports Travel Offer", es: "La Oferta Airsports Travel" })} />
    </MiniSite>
  );
}
